import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  Lock,
  Zap,
} from "lucide-react";

export const KyuStoreSwissPaper: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const sp = (delay: number, damping = 16, stiffness = 120) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Swiss Editorial Architectural Tokens
  const paperBg = "#F4F1EA";
  const inkDark = "#121316";
  const inkMuted = "#646770";
  const hairline = "rgba(18, 19, 22, 0.15)";
  const accentCobalt = "#1E40AF";
  const accentEmerald = "#059669";

  // Browser Mockup Staging Motion
  const stageSpring = sp(5, 18, 100);
  const stageY = interpolate(stageSpring, [0, 1], [60, 0]);

  // Receipt Card Slide-In (from frame 25)
  const receiptSpring = sp(25, 14, 130);
  const receiptY = interpolate(receiptSpring, [0, 1], [80, 0]);

  // Subtitles Safe Zone
  const SUBTITLES = [
    { from: 0, to: 120, text: "Masih jaman top-up game nunggu admin bales berjam-jam?", highlight: "nunggu admin" },
    { from: 120, to: 270, text: "Udah nunggu lama... salah ketik ID pula. Nih, web baru Kyu Store!", highlight: "web baru Kyu Store" },
    { from: 270, to: 480, text: "Tampilan baru jauh lebih kenceng, cukup ketik User ID langsung kedeteksi!", highlight: "langsung kedeteksi" },
    { from: 480, to: 690, text: "Bayar tinggal scan QRIS, 0.8 detik saldo langsung mendarat otomatis!", highlight: "0.8 detik" },
    { from: 690, to: 935, text: "Gak pake ribet login, gak pake password. Cobain di kyustore.com!", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: paperBg,
        color: inkDark,
        fontFamily: "'Rajdhani', -apple-system, sans-serif",
        overflow: "hidden",
        padding: "0 60px",
      }}
    >
      {/* 1. Swiss Editorial Architectural Lines */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 60,
          width: 1,
          backgroundColor: hairline,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 60,
          width: 1,
          backgroundColor: hairline,
        }}
      />

      {/* 2. Top Header Magazine Bar (Y: 260px) */}
      <div
        style={{
          position: "absolute",
          top: 260,
          left: 60,
          right: 60,
          borderBottom: `2px solid ${inkDark}`,
          paddingBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.15em", color: inkMuted, textTransform: "uppercase" }}>
            [ 01 // PLATFORM SHOWCASE ]
          </div>
          <div style={{ fontSize: 44, fontWeight: 900, textTransform: "uppercase", letterSpacing: "-0.02em", color: inkDark, lineHeight: 1 }}>
            KYUSTORE.COM
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: accentCobalt, letterSpacing: "0.1em" }}>
            EDISI WEB TERBARU 2026
          </span>
        </div>
      </div>

      {/* 3. Swiss Headline */}
      <div
        style={{
          position: "absolute",
          top: 350,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 72, fontWeight: 900, textTransform: "uppercase", lineHeight: 0.95, letterSpacing: "-0.02em" }}>
          TOP-UP GAME <br />
          <span style={{ color: accentCobalt }}>SERBA INSTAN</span>
        </div>
      </div>

      {/* 4. Real Desktop Browser Window Mockup (Y: 520 - 1060 px) */}
      <div
        style={{
          position: "absolute",
          top: 520,
          left: 60,
          right: 60,
          transform: `translateY(${stageY}px)`,
          backgroundColor: "#FFFFFF",
          borderRadius: 24,
          border: `1.5px solid ${inkDark}`,
          boxShadow: "0 20px 40px -10px rgba(18, 19, 22, 0.12)",
          overflow: "hidden",
        }}
      >
        {/* Browser Top Chrome */}
        <div
          style={{
            height: 48,
            backgroundColor: "#F8F6F0",
            borderBottom: `1.5px solid ${inkDark}`,
            display: "flex",
            alignItems: "center",
            padding: "0 18px",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#DC2626" }} />
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#F59E0B" }} />
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#10B981" }} />
          </div>
          <div
            style={{
              flex: 1,
              height: 28,
              backgroundColor: "#FFFFFF",
              borderRadius: 8,
              border: "1px solid rgba(18, 19, 22, 0.15)",
              display: "flex",
              alignItems: "center",
              padding: "0 10px",
              gap: 8,
            }}
          >
            <Lock size={12} color={inkMuted} />
            <span style={{ fontSize: 13, fontWeight: 700, color: inkDark, fontFamily: "monospace" }}>
              https://kyustore.com/topup
            </span>
          </div>
        </div>

        {/* Real Screenshot Inside Browser */}
        <div style={{ height: 380, overflow: "hidden", position: "relative" }}>
          <Img
            src={staticFile("kyustore_real_desktop.png")}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "cover",
            }}
          />
        </div>
      </div>

      {/* 5. Minimalist Swiss Receipt Ticket (Y: 1000 - 1380 px) */}
      <div
        style={{
          position: "absolute",
          top: 990,
          left: 60,
          right: 60,
          transform: `translateY(${receiptY}px)`,
          backgroundColor: "#FFFFFF",
          border: `2px solid ${inkDark}`,
          borderRadius: 20,
          padding: 24,
          boxShadow: "8px 8px 0px rgba(18, 19, 22, 1)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px dashed ${hairline}`, paddingBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Zap size={22} color={accentCobalt} />
            <span style={{ fontSize: 24, fontWeight: 900, textTransform: "uppercase" }}>
              0.8S QRIS SETTLEMENT
            </span>
          </div>
          <span style={{ fontSize: 16, fontWeight: 800, color: accentEmerald, background: "#ECFDF5", padding: "4px 10px", borderRadius: 6 }}>
            AUTOMATIC DISPATCH
          </span>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: inkMuted, textTransform: "uppercase" }}>
              VERIFIKASI ID
            </div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>
              AUTO DETEKSI NICKNAME
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: inkMuted, textTransform: "uppercase" }}>
              KEAMANAN AKUN
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: accentEmerald }}>
              TANPA PASSWORD
            </div>
          </div>
        </div>
      </div>

      {/* 6. Subtitles Safe Zone (Bottom 390px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 390,
            left: 60,
            right: 60,
            display: "flex",
            justifyContent: "center",
            zIndex: 20,
          }}
        >
          <div
            style={{
              backgroundColor: inkDark,
              color: "#FFFFFF",
              borderRadius: 14,
              padding: "16px 28px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.2 }}>
              {currentSub.text.split(currentSub.highlight).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span
                      style={{
                        color: "#93C5FD",
                        borderBottom: "2px solid #93C5FD",
                        paddingBottom: 2,
                      }}
                    >
                      {currentSub.highlight}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* 7. Master Audio Sync */}
      <Audio
        src={staticFile("kyustore_web_promo_enceladus.mp3")}
        volume={1.0}
      />
    </AbsoluteFill>
  );
};
